import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBox, MiDatePicker, MiDetailFields } from "@miplatform/components";
import {
  DOCUMENT_TYPES,
  INPUT_TYPES,
  PERMISSIONS,
} from "@miplatform/constants";
import { IDocumentListItemResponse } from "@miplatform/types";
import { externalReferenceId } from "@miplatform/utils";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { InputFile } from "./components/InputFile/InputFile";
import { UploadDocumentModal } from "../../../components/UploadDocumentModal/UploadDocumentModal";
import { BidDocumentViewer } from "../BidDocumentViewer/BidDocumentViewer";
import { useCreateBidContext } from "../CreateBidContext";
import { DocCategory } from "../CreateBids.types";
import { BID_STATUS, DOC_CATEGORY } from "../constants";
import { useBidDetailsFormViewModel } from "./useBidDetailsFormViewModel";

const NONE_OPTION = [{ name: "None", id: null }];
const NONE_ASSIGNEE = [{ firstName: "None", lastName: "", id: null }];

export function BidDetailsForm() {
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [docCategory, setDocCategory] = useState<DocCategory | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentLabel, setDocumentLabel] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [city, setCity] = useState("");

  const { classes, theme } = useStyles();
  const { form, communities, accounts, seriesGroups, setOpenDocumentViewer } =
    useCreateBidContext();
  const { control, setValue, watch } = form;
  const {
    id,
    documents = [],
    communityId,
    builderId,
    seriesGroupId,
    bidRequest,
    planStatus,
  } = watch();
  const {
    loading,
    contacts,
    series,
    departments,
    getAllData,
    fetchSeries,
    uploadDocument,
    removeDocument,
  } = useBidDetailsFormViewModel();
  const { hasPermission } = usePermissions();

  const canAssignBids = hasPermission(PERMISSIONS.PLAN.ASSIGN);

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    if (builderId) {
      fetchSeries(builderId, seriesGroupId || "");
    }
  }, [builderId, seriesGroupId]);

  useEffect(() => {
    const community = communities.find((c) => c.id === communityId);
    setCity(community?.city?.cityName ?? "");
  }, [communityId]);

  const planDoc = documents.find((d) => d.category === DOC_CATEGORY.PLAN);
  const specDoc = documents.find((d) => d.category === DOC_CATEGORY.SPEC);
  const otherDocs = documents.filter(
    (d) => d.category !== DOC_CATEGORY.PLAN && d.category !== DOC_CATEGORY.SPEC
  );
  const bidApproved = planStatus === BID_STATUS.APPROVED;

  const handleOpenUploadModal = (category: DocCategory) => {
    setDocCategory(category);
    setDocumentLabel(category === DOC_CATEGORY.OTHER ? "" : category);
    setSelectedFile(null);
    setOpenUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setDocCategory(null);
    setSelectedFile(null);
    setDocumentLabel("");
    setOpenUploadModal(false);
  };

  const handleUploadFile = async () => {
    if (!selectedFile || !docCategory) return;
    const category =
      docCategory === DOC_CATEGORY.OTHER ? documentLabel : docCategory;
    const uploadedDoc = await uploadDocument({
      file: selectedFile,
      documentType: DOCUMENT_TYPES.UNKNOWN,
      category,
      externalReferenceId: externalReferenceId.getBidRefId({ bidId: id }),
    });
    if (!uploadedDoc) return;
    const addedDoc = [...documents, uploadedDoc] as IDocumentListItemResponse[];
    setValue("documents", addedDoc, { shouldValidate: true });
    handleCloseUploadModal();
  };

  const handleRemoveFile = async (doc: IDocumentListItemResponse) => {
    const success = await removeDocument(doc);
    if (!success) return;
    const removedDoc = documents.filter((d) => d.id !== doc.id);
    setValue("documents", removedDoc, { shouldValidate: true });
  };

  const handleViewFile = (doc: IDocumentListItemResponse) => {
    setDocumentId(doc.id);
    setOpenDocumentViewer(true);
  };

  const bidDetailFields = [
    {
      label: "Bid Name",
      name: "name",
      formControl: control,
      width: "50%",
      required: true,
      readOnly: bidApproved,
    },
    {
      label: "Customer",
      name: "builderId",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      width: "50%",
      required: true,
      disabled: true,
      readOnly: bidApproved,
      options: accounts.map((a) => ({ label: a.name, value: a.id })),
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("builderId", e.target.value, { shouldValidate: true });
        setValue("seriesGroupId", null, { shouldDirty: true });
        setValue("seriesId", null, { shouldDirty: true });
      },
    },
    {
      label: "Community",
      name: "communityId",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      width: "50%",
      readOnly: bidApproved,
      options: [...NONE_OPTION, ...communities].map((c) => ({
        label: c.name,
        value: c.id,
      })),
    },
    {
      label: "City",
      width: "50%",
      value: city,
      disabled: true,
      readOnly: bidApproved,
    },
    {
      label: "Request Date",
      name: "bidRequest.requestDate",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      disabled: true,
      customComponent: (
        <MiDatePicker
          isFullWidth
          value={bidRequest?.requestDate}
          readOnly={bidApproved}
          noBorder={bidApproved}
        />
      ),
    },
    {
      label: "Due Date",
      name: "bidRequest.dueDate",
      fieldType: INPUT_TYPES.CUSTOM,
      formControl: control,
      width: "50%",
      required: true,
      disabled: true,
      customComponent: (
        <MiDatePicker
          isFullWidth
          value={bidRequest?.dueDate}
          readOnly={bidApproved}
          noBorder={bidApproved}
        />
      ),
    },
    {
      label: "Requester",
      name: "bidRequest.requesterId",
      width: "50%",
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      disabled: true,
      readOnly: bidApproved,
      options: contacts.map((u) => ({
        label: `${u.firstName} ${u.lastName}`,
        value: u.id,
      })),
    },
    {
      label: "Assignee",
      name: "assigneeId",
      width: "50%",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      disabled: !canAssignBids,
      readOnly: bidApproved,
      options: [...NONE_ASSIGNEE, ...departments.flatMap((d) => d.users)].map(
        (u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u.id,
        })
      ),
    } as any,
  ];

  const bidSeriesFields = [
    {
      label: "Series Group",
      name: "seriesGroupId",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      formControl: control,
      readOnly: bidApproved,
      options: [...NONE_OPTION, ...seriesGroups].map((s) => ({
        label: s.name,
        value: s.id,
      })),
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("seriesGroupId", e.target.value, { shouldDirty: true });
        setValue("seriesId", null, { shouldDirty: true });
      },
    },
    {
      label: "Series",
      name: "seriesId",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      readOnly: bidApproved,
      formControl: control,
      options: [...NONE_OPTION, ...series].map((s) => ({
        label: s.name,
        value: s.id,
      })),
    },
  ];

  const bidDocumentsFields = [
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: (
        <InputFile
          doc={planDoc}
          handleRemoveFile={handleRemoveFile}
          handleOpenUploadModal={() => handleOpenUploadModal(DOC_CATEGORY.PLAN)}
          handleViewFile={handleViewFile}
          label="Upload Plan File"
          required
          readOnly={bidApproved}
        />
      ),
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: (
        <InputFile
          doc={specDoc}
          handleRemoveFile={handleRemoveFile}
          handleOpenUploadModal={() => handleOpenUploadModal(DOC_CATEGORY.SPEC)}
          handleViewFile={handleViewFile}
          label="Upload Specification File"
          required
          readOnly={bidApproved}
        />
      ),
    },
    ...otherDocs.map((otherDoc) => ({
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: (
        <InputFile
          doc={otherDoc}
          handleRemoveFile={handleRemoveFile}
          handleViewFile={handleViewFile}
          readOnly={bidApproved}
        />
      ),
    })),
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent:
        !bidApproved && otherDocs.length < 2 ? (
          <InputFile
            handleOpenUploadModal={() =>
              handleOpenUploadModal(DOC_CATEGORY.OTHER)
            }
            label="Upload Other Files"
          />
        ) : (
          <div />
        ),
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      width: "50%",
      customComponent: <div />,
    } as any,
  ];

  const bidAdditionDetailsFields = [
    {
      label: "Additional House Features (e.g. Front Entry - Electric)",
      name: "additionalFeatures",
      formControl: control,
      width: "50%",
      readOnly: bidApproved,
    },
    {
      label: "Pipe Brand",
      name: "pipeBrand",
      formControl: control,
      width: "50%",
      readOnly: bidApproved,
    },
  ];

  return (
    <Box className={classes.root}>
      <Box className={classes.contentBox}>
        <MiBox headerTitle="Bid Details" sx={{ overflow: "visible" }}>
          <MiDetailFields
            detailFields={bidDetailFields}
            headerProps={{
              title: "Details",
              variant: "text",
              style: { padding: theme.spacing(0, 0, 0, 1) },
            }}
          />
          <MiDetailFields
            detailFields={bidSeriesFields}
            headerProps={{
              title: "Series",
              variant: "text",
              style: { padding: theme.spacing(0, 0, 0, 1) },
            }}
          />
          <MiDetailFields
            detailFields={bidDocumentsFields}
            headerProps={{
              title: "Documents",
              variant: "text",
              style: { padding: theme.spacing(0, 0, 0, 1) },
            }}
          />
        </MiBox>
        <MiBox headerTitle="Additional Details" sx={{ marginTop: 0 }}>
          <MiDetailFields detailFields={bidAdditionDetailsFields} />
        </MiBox>
      </Box>
      <BidDocumentViewer documentId={documentId} />
      <UploadDocumentModal
        open={openUploadModal}
        docCategory={docCategory}
        documentLabel={documentLabel}
        selectedFile={selectedFile}
        loading={loading}
        handleCloseUploadModal={handleCloseUploadModal}
        handleUploadFile={handleUploadFile}
        setDocumentLabel={setDocumentLabel}
        setSelectedFile={setSelectedFile}
      />
    </Box>
  );
}

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    overflow: "hidden",
    flex: 1,
  },
  contentBox: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    flexDirection: "column",
  },
}));
